import { z } from "zod";
import type { EscalationRiskTier } from "@remi/enums/escalation-risk-tier";
import type { EscalationStage } from "@remi/enums/escalation-stage";
import { isOneOf } from "@remi/ts-utils";

export const EscalationBannerStatusValueSchema = z.enum(["active", "previously-escalated", "none"]);
export type EscalationBannerStatusValue = z.infer<typeof EscalationBannerStatusValueSchema>;

// NOT_CONTACTED is the risk scorer's untriaged alert, not yet an escalation: a hot-tier work site
// counts as escalated only once someone has picked it up and until it is resolved or dismissed.
export const ACTIVE_ESCALATION_STAGES = [
  "PENDING_CONTACT",
  "PENDING_RESOLUTION",
  "RESOLVED_PENDING_COMPLETION",
] as const satisfies readonly EscalationStage[];

// Single source of truth for the web banner. Tier inputs must be the WorkSite's values, not the
// Escalation row's — the current tier lives on WorkSite (rescore cron), the Escalation row lags.
export function getEscalationBannerStatus(input: {
  resolutionStage: EscalationStage;
  riskTier: EscalationRiskTier | null;
  riskTierOverride: EscalationRiskTier | null;
}): EscalationBannerStatusValue {
  const effectiveTier = input.riskTierOverride ?? input.riskTier;
  if (effectiveTier !== "CRITICAL" && effectiveTier !== "AT_RISK") {
    return "none";
  }
  if (input.resolutionStage === "COMPLETION") {
    return "previously-escalated";
  }
  return isOneOf(input.resolutionStage, ACTIVE_ESCALATION_STAGES) ? "active" : "none";
}

// Nearly all NOT_CONTACTED escalations are auto-scored below AT_RISK, so that stage alone keeps the
// tier gate; without it the field banner would mostly be noise.
export function getFieldEscalationStatus(input: {
  resolutionStage: EscalationStage;
  riskTier: EscalationRiskTier | null;
  riskTierOverride: EscalationRiskTier | null;
}): EscalationBannerStatusValue {
  if (input.resolutionStage === "DISMISSED") {
    return "none";
  }
  if (input.resolutionStage === "COMPLETION") {
    return "previously-escalated";
  }
  if (input.resolutionStage !== "NOT_CONTACTED") {
    return "active";
  }
  const effectiveTier = input.riskTierOverride ?? input.riskTier;
  return effectiveTier === "CRITICAL" || effectiveTier === "AT_RISK" ? "active" : "none";
}
